import { Color } from 'services/apiTypes/card.types'
import { DeckTagRelation } from 'services/apiTypes/deckTag.types'
import { CardType, DEPRICATED_FORMAT_NAMES, EDH_FORMATS, FormatType, HUMAN_READABLE_FORMAT, StackType } from 'types/deck'

import { deltaToString } from 'utils/Deltas'
import { colorsToColorIdentityName } from 'utils/colorUtils'

export const generateDeckDescription = (
  name: string,
  format: FormatType,
  deckDescription: string,
  deckTags: DeckTagRelation[],
  commanders: CardType[],
  stacks: { mainDeck: StackType[]; sideDeck: StackType[] },
): { title: string; description: string } => {
  const humanFormat = HUMAN_READABLE_FORMAT[format] || DEPRICATED_FORMAT_NAMES[format]
  const readableDescription = deltaToString(deckDescription)
  const readableTags = getHumanDeckTags(deckTags)
  const commanderNames = commanders.map(card => card.name).join(' & ')
  const stackHeaders = stacks.mainDeck.reduce((acc, stack) => {
    if (!acc) return `(${stack.quantity}) ${stack.name}`

    return `${acc} • (${stack.quantity}) ${stack.name}`
  }, '')

  // @ts-ignore - Object.keys returns strings, it's fine
  const colors: Color[] = Object.keys(
    commanders.reduce((acc, c) => {
      const colors = EDH_FORMATS.includes(format) ? c.colorIdentity : c.colors

      for (const color of colors) {
        acc[color] = true
      }

      return acc
    }, {} as Record<Color, boolean>),
  )

  let colorName: string = colorsToColorIdentityName(colors)

  if (colorName !== 'Colorless') {
    if (['White', 'Blue', 'Black', 'Red', 'Green'].includes(colorName)) colorName = `Mono-${colorName}`

    colorName = `${colorName} ` // Adds space for concat later
  } else colorName = '' // Colorless is what returns if we have no colors (which we won't if we have no commanders). AkA - this won't show anything without commanders (which is what I want)

  let title = ''
  let description = ''

  title += name || 'Sandbox'

  if (humanFormat) title += ` • (${colorName}${humanFormat} deck)`
  else title += ` (Card package)`

  // prettier-ignore
  description += `${commanderNames ? `${commanderNames} - ` : ''}${humanFormat ? `${humanFormat} deck` : 'Card Package'} ${readableTags} \n${stackHeaders} \n${readableDescription}`
  title += ` • Archidekt`

  return { title, description }
}

export const getHumanDeckTags = (deckTags: DeckTagRelation[]) => {
  if (!deckTags.length) return '\n'

  const tagNames = deckTags.map(tag => tag.name)

  return `\n${tagNames.join(' • ')}`
}

export const convertNameToURLSafeName = (name: string) =>
  name
    .replace(/[^A-Za-z0-9\s]/g, '')
    .replace(/  +/g, ' ')
    .replace(/ /g, '_')
    .toLocaleLowerCase()

export const generateDeckUrl = (id: number | string, name?: string) => {
  if (!name) return `/decks/${id}`

  return `/decks/${id}/${name ? convertNameToURLSafeName(name) : ''}`
}

export const generateSnapshotUrl = (id: number | string, name?: string) => {
  if (!name) return `/snapshots/${id}`

  return `/snapshots/${id}/${name ? convertNameToURLSafeName(name) : ''}`
}
